import type { LocalRecord } from '@dovefiniscono/sync';
import { toISODate } from '../lib/dates';
import { formatCents } from '../lib/money';
import { isWithin } from '../money/summary';

// Rules "when… then…": an invoice collected puts a share aside for taxes, a salary is split
// among savings goals, a card with a label becomes a draft invoice. Each rule acts once per event,
// with a run whose id is made from the rule and the event, so two devices do not act twice.

export type Rule = LocalRecord<'rules'>;
type Invoice = LocalRecord<'invoices'>;
type Transaction = LocalRecord<'transactions'>;
type Task = LocalRecord<'tasks'>;

export type RuleData = {
  invoices: readonly Invoice[];
  transactions: readonly Transaction[];
  tasks: readonly Task[];
  /** Subcategory → the one above: a rule on "Lavoro" also takes "Lavoro › Stipendio". */
  parentOf: ReadonlyMap<string, string>;
};

/** One thing that happened, that a rule may act on. */
export type RuleEvent = {
  id: string;
  day: string;
  /** Positive cents, or 0 when there is no amount (a card). */
  amount: number;
  label: string;
};

const dayOf = (instant: string) => toISODate(new Date(instant));

/** What a rule listens to, from the day it was made: older events are left alone. */
export function eventsFor(rule: Rule, data: RuleData): RuleEvent[] {
  const since = dayOf(rule.createdAt);
  const min = rule.filter.minAmount ?? 0;
  if (rule.trigger === 'invoicePaid') {
    return data.invoices
      .filter((invoice) => invoice.paidOn !== null && !invoice.draft)
      .filter((invoice) => (invoice.paidOn as string) >= since && invoice.amount >= min)
      .map((invoice) => ({
        id: invoice.id,
        day: invoice.paidOn as string,
        amount: invoice.amount,
        label: `Fattura ${invoice.client}`,
      }));
  }
  if (rule.trigger === 'incomeReceived') {
    const category = rule.filter.categoryId ?? null;
    return data.transactions
      .filter((transaction) => transaction.amount > 0 && transaction.occurredOn >= since)
      .filter((transaction) => transaction.amount >= min)
      .filter(
        (transaction) =>
          category === null ||
          (transaction.categoryId !== null &&
            isWithin(transaction.categoryId, category, data.parentOf)),
      )
      .map((transaction) => ({
        id: transaction.id,
        day: transaction.occurredOn,
        amount: transaction.amount,
        label: transaction.note || 'Entrata',
      }));
  }
  const label = rule.filter.labelId ?? null;
  return data.tasks
    .filter((task) => task.completedAt !== null && dayOf(task.completedAt) >= since)
    .filter((task) => label === null || (task.labelIds ?? []).includes(label))
    .map((task) => ({
      id: task.id,
      day: dayOf(task.completedAt as string),
      amount: 0,
      label: task.title,
    }));
}

/** The shares of an amount, in whole cents; what rounding leaves goes to the first. */
export function shares(amount: number, allocations: readonly { goalId: string; share: number }[]) {
  const parts = allocations.map((allocation) => ({
    goalId: allocation.goalId,
    cents: Math.floor((amount * allocation.share) / 10_000),
  }));
  const wanted = Math.round(
    (amount * allocations.reduce((sum, allocation) => sum + allocation.share, 0)) / 10_000,
  );
  const given = parts.reduce((sum, part) => sum + part.cents, 0);
  if (parts[0] && wanted > given) parts[0].cents += wanted - given;
  return parts.filter((part) => part.cents > 0);
}

/** The events each enabled rule has still to act on. */
export function pendingRuns(
  rules: readonly Rule[],
  data: RuleData,
  hasRun: (ruleId: string, eventId: string) => boolean,
) {
  return rules
    .filter((rule) => rule.enabled)
    .flatMap((rule) =>
      eventsFor(rule, data)
        .filter((event) => !hasRun(rule.id, event.id))
        .map((event) => ({ rule, event })),
    );
}

const TRIGGER_WORDS: Record<Rule['trigger'], string> = {
  invoicePaid: 'Quando incassi una fattura',
  incomeReceived: 'Quando ricevi un’entrata',
  taskCompleted: 'Quando completi una scheda',
};

/** "Quando incassi una fattura → 25% in Tasse": a rule in words. */
export function describeRule(
  rule: Pick<Rule, 'trigger' | 'filter' | 'action' | 'params'>,
  names: {
    goal: (id: string) => string;
    category: (id: string) => string;
    label: (id: string) => string;
  },
): string {
  const when = [TRIGGER_WORDS[rule.trigger]];
  if (rule.filter.categoryId) when.push(`di ${names.category(rule.filter.categoryId)}`);
  if (rule.filter.labelId) when.push(`con l’etichetta ${names.label(rule.filter.labelId)}`);
  if (rule.filter.minAmount)
    when.push(`da almeno ${formatCents(rule.filter.minAmount, { round: true })}`);
  const then =
    rule.action === 'setAside'
      ? (rule.params.allocations ?? [])
          .map(
            (allocation) =>
              `${(allocation.share / 100).toLocaleString('it-IT')}% in ${names.goal(allocation.goalId)}`,
          )
          .join(', ')
      : `bozza di fattura${rule.params.client ? ` per ${rule.params.client}` : ''}${
          rule.params.amount ? ` da ${formatCents(rule.params.amount)}` : ''
        }`;
  return `${when.join(' ')} → ${then || 'niente'}`;
}
